import { ChevronDown, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import type {
  CombinedNavSnapshotDto,
  PortfolioMetricsDto,
  SnapshotDto,
} from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';

import { NotificationCenter } from './NotificationCenter';

interface TopStatusBarProps {
  snapshot: SnapshotDto | CombinedNavSnapshotDto | null | undefined;
  metrics?: PortfolioMetricsDto | null | undefined;
  currency: string;
  showNotifications?: boolean;
}

export function TopStatusBar({
  currency,
  metrics,
  showNotifications = true,
  snapshot,
}: TopStatusBarProps) {
  const [expanded, setExpanded] = useState(false);
  const snapshotCurrency = snapshot?.currency ?? currency;
  const fxRate = snapshot?.fxRate ?? null;
  const sourceCurrency = snapshot?.sourceCurrency ?? null;
  const fxRateSource = snapshot?.fxRateSource ?? null;
  const fxBadge =
    fxRate && sourceCurrency
      ? {
          label: `fx ${Number(fxRate).toFixed(2)}`,
          title: `Converted from ${sourceCurrency} at ${fxRate} (source: ${sourceLabel(
            fxRateSource,
          )})`,
        }
      : null;

  return (
    <div className="sticky top-0 z-10 border-b border-border bg-bg/95 px-4 py-3 backdrop-blur sm:px-6">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between gap-4">
          <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-5">
            <StatusMetric
              badge={fxBadge}
              label="NAV"
              value={formatCurrency(snapshot?.nav, snapshotCurrency)}
            />
            <StatusMetric
              label="Day P&L"
              value={formatCurrency(metrics?.dayPnl, snapshotCurrency)}
            />
            <StatusMetric
              className="hidden sm:block"
              label="Cash"
              value={formatCurrency(snapshot?.cash, snapshotCurrency)}
            />
            <StatusMetric
              className="hidden sm:block"
              label="Margin Used"
              value={formatCurrency(snapshot?.marginUsed, snapshotCurrency)}
            />
            <div className="hidden sm:block">
              <TradeLock />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface text-text sm:hidden"
              aria-label={expanded ? 'Collapse account status' : 'Expand account status'}
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
            >
              <ChevronDown
                className={`h-5 w-5 transition ${expanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            {showNotifications ? <NotificationCenter /> : null}
          </div>
        </div>
        {expanded ? (
          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3 sm:hidden">
            <StatusMetric label="Cash" value={formatCurrency(snapshot?.cash, snapshotCurrency)} />
            <StatusMetric
              label="Margin Used"
              value={formatCurrency(snapshot?.marginUsed, snapshotCurrency)}
            />
            <div className="col-span-2">
              <TradeLock />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TradeLock() {
  return (
    <>
      <div className="text-xs uppercase tracking-wide text-text-muted">Trade Lock</div>
      <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-success/40 bg-success/10 px-2 py-1 text-sm font-medium text-success">
        <ShieldCheck className="h-4 w-4" aria-hidden="true" />
        Active
      </div>
    </>
  );
}

function StatusMetric({
  badge,
  className,
  label,
  value,
}: {
  badge?: { label: string; title: string } | null;
  className?: string;
  label: string;
  value: string;
}) {
  return (
    <div className={className}>
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-1 flex min-w-0 items-center gap-2">
        <div className="truncate text-sm font-semibold tabular-nums text-text">{value}</div>
        {badge ? (
          <span
            className="border-warning/40 bg-warning/10 text-warning shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium tabular-nums"
            title={badge.title}
          >
            {badge.label}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function sourceLabel(source: string | null): string {
  if (source === 'IDEALFX_TRADE') return 'IDEALFX trade';
  if (source === 'EXTERNAL_API') return 'external API';
  return source ?? 'unknown';
}
